import { useRouter } from 'next/router';
import { useLang } from '@/i18n';
import EcoImg from 'eco-design-components/base/eco-img';
import { useState, useEffect } from 'react';
import css from './index.module.scss';
import { getSolarRecommand } from '@/service/solar-calculator';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, EffectFade } from 'swiper';
import { Swiper as SwiperType } from 'swiper/types';
import { FormatMoneyLocal } from '@elem/ui-common/utils/FormatMoneyLocal';
import EcoLink from 'eco-design/base/eco-link';
import { Amazon } from './icons';
import Dialog from './Dialog';
import gtm from '@/utils/gtm';
import { useScheduledContent } from '@/utils/hooks/useScheduledContent';
import { ItfInit, ItfAPI, ItfBtn, ItfData, ItfInfo, ItfSwiper, ItfItem } from './type';
import { dataAll, dataInfo, colorMapping } from './data';
import { useProductDispatch } from '../../../../ProductProvider';

const C: React.FC = () => {
    const { locale = 'us' } = useRouter();
    const { t } = useLang('theProduct.C');
    const data = dataAll[locale as keyof typeof dataAll] || dataAll.us;
    const [items, setItems] = useState<ItfItem[]>([]);
    const [groups, setGroups] = useState<ItfItem[][]>([]);

    const [activeItem, setActiveItem] = useState<ItfItem>({} as ItfItem);
    const [activePId, setActivePId] = useState<string>('');
    const [active, setActive] = useState<number>(0);

    const [multiId, setMultiId] = useState(0);

    const [swiper, setSwiper] = useState({
        realIndex: 0,
        slideTo: (_: number) => {},
        slidePrev: () => {},
        slideNext: () => {},
    } as SwiperType);

    const dispatch = useProductDispatch();

    const curStage = useScheduledContent({
        '2025/01/01 16:00:00 GMT+8': 1,
        '2025/11/13 16:00:00 GMT+8': 2,
        '2025/11/20 16:00:00 GMT+8': 3,
        '2025/11/24 16:00:00 GMT+8': 4,
        '2025/12/31 16:00:00 GMT+8': 5,
    });

    const handleSlideChange = () => {
        setActive(swiper?.realIndex);
    };

    const handleHover = (item: any) => {
        if (item.productId !== activeItem.productId) {
            setActiveItem(item);
            swiper?.slideTo(0);
            setActivePId('');
            setTimeout(() => {
                setActivePId(item.productId);
            }, 200);
        }
    };

    const handleGTM = (num: number) => {
        const curIdx: number = activeItem.data?.findIndex((item: ItfData) => item.color === activeItem.color);
        if (curIdx !== -1) {
            const tempGroups: ItfItem[][] = JSON.parse(JSON.stringify(groups));
            if (num === 1 && curIdx > 0) {
                tempGroups?.map((group: ItfItem[]) => {
                    group?.map((item: ItfItem) => {
                        if (item.productId === activeItem.productId && item.data?.[curIdx - 1]) {
                            item.color = item.data?.[curIdx - 1].color;
                            setActiveItem(item);
                        }
                    });
                });
            }
            if (num === 2 && curIdx < activeItem.data.length - 1) {
                tempGroups?.map((group: ItfItem[]) => {
                    group?.map((item: ItfItem) => {
                        if (item.productId === activeItem.productId && item.data?.[curIdx + 1]) {
                            item.color = item.data?.[curIdx + 1].color;
                            setActiveItem(item);
                        }
                    });
                });
            }
            setGroups(tempGroups);
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_detail_page',
            list_name: t('title'),
            button_name: `${num === 1 ? 'Left switch' : num === 2 ? 'Right switch' : 'none'}`,
            __btn_id1: `rapid-mag-C .swiper-button-${num === 1 ? 'prev' : num === 2 ? 'next' : 'none'}`,
            __btn_id2: num === 1 ? '<' : '>',
            __btn_type: 'common',
        });
    };

    const handleColorChange = (e: React.MouseEvent, groupIdx: number, list: ItfData, c: ItfSwiper, o: number, cls: string) => {
        e.stopPropagation();
        e.preventDefault();
        const tempGroups = JSON.parse(JSON.stringify(groups));
        const targetItem = tempGroups[groupIdx].find((item: ItfItem) => item.productId === list.productId);
        if (targetItem) {
            targetItem.color = c.color;
            setGroups(tempGroups);
            swiper?.slideTo(o);
        }
        gtm.push({
            event: 'uaEvent',
            event_name: 'product_detail_page',
            list_name: t('title'),
            button_name: `${list.productTitle}_${c.color || list.skuTitle}`,
            __btn_id1: `rapid-mag-C ${cls}`,
            __btn_id2: c.color,
            __btn_type: 'common',
        });
    };

    const handleOpenDialog = (e: React.MouseEvent, multiId: number, cls: string) => {
        e.stopPropagation();
        e.preventDefault();
        setMultiId(multiId);
        gtm.push({
            __btn_id1: `rapid-mag-C ${cls}`,
            __btn_id2: t('notify-me'),
            __btn_type: 'text',
        });
    };

    const getAsynItem = async (item: ItfInit) => {
        const { productId, variantId } = item;
        if (!productId || !variantId) {
            return item;
        }
        if ([1, 2].includes(curStage as number) && item.allowLearnStage === 3) {
            return {
                ...{
                    compareAtPrice: 0,
                    handle: '',
                    img: '',
                    inventoryPolicy: 'deny',
                    inventoryQuantity: 0,
                    price: 0,
                    productTitle: '',
                    sku: '',
                    skuTitle: '',
                    status: '',
                    variantImg: '',
                },
                ...item,
            } as ItfInit;
        }
        return await new Promise(async (resolve, reject) => {
            try {
                const res = await getSolarRecommand({ productId, variantId }, locale);
                if (res?.code === '0') {
                    return resolve({ ...item, ...res?.data });
                } else {
                    console.error(res?.message);
                    return reject(res?.message);
                }
            } catch (e) {
                console.error(e);
                return reject(e);
            }
        });
    };

    const generateItfDataArr = (arr: ItfAPI[]): ItfData[] => {
        const temps: ItfData[] = [];
        arr.forEach((rw: ItfAPI) => {
            // const itemObj = { ...rw, color: rw.skuTitle.toLowerCase() } as ItfData;
            const itemObj = { ...rw } as ItfData;
            const btnPreorder = itemObj.btns.find((btn: ItfBtn) => btn.type === 'preorder');
            if (btnPreorder && !btnPreorder.link) {
                btnPreorder.link = `https://${locale}.ecoflow.com/cart/${rw.variantId}:1?channel=buy_button`;
            }
            const btnBuy = itemObj.btns.find((btn: ItfBtn) => btn.type === 'buy');
            if (btnBuy && !btnBuy.link) {
                btnBuy.link = `https://${locale}.ecoflow.com/cart/${rw.variantId}:1?channel=buy_button`;
            }
            const btnLearn = itemObj.btns.find((btn: ItfBtn) => btn.type === 'learn');
            if (btnLearn && !btnLearn.link) {
                btnLearn.link = `https://${locale}.ecoflow.com/products/${rw.handle}?variant=${rw.variantId}`;
            }
            temps.push(itemObj);
        });
        dispatch({ type: 'changeProductInfo', payload: temps });
        return temps;
    };

    const generateItfItemArr = (arr: ItfData[]): ItfItem[] => {
        const temps: ItfItem[] = [];
        arr.forEach((rw: ItfData) => {
            const target: ItfItem | undefined = temps.find((item: ItfItem) => item.productId === rw.productId);
            if (target) {
                target.data.push({ ...rw });
                target.swiper.push({ color: rw.color, img: rw.variantImg });
            } else {
                const itemObj = {} as ItfItem;
                itemObj.productId = rw.productId;
                itemObj.color = rw.color;
                itemObj.data = [{ ...rw }];
                itemObj.swiper = [{ color: rw.color, img: rw.variantImg }];
                itemObj.info = dataInfo[rw.productId] || [];
                const stageArr = rw.stageMapping?.[curStage as number] || [''];
                if (stageArr?.[0]) {
                    temps.push(itemObj);
                }
            }
        });
        setActiveItem(temps[0]);
        setActivePId(temps[0].productId);
        setItems(temps);
        return temps;
    };

    const generateItfGroupArr = (arr: ItfItem[]): ItfItem[][] => {
        const temps: ItfItem[][] = arr?.length > 6 ? [[...arr.slice(0, 6)], [...arr.slice(6)]] : [arr];
        setGroups(temps);
        return temps;
    };

    useEffect(() => {
        const tempArr = JSON.parse(JSON.stringify(data));
        const promises: Promise<ItfAPI>[] = tempArr?.map((item: ItfInit) => getAsynItem(item));
        Promise.all(promises)
            .then((res: ItfAPI[]) => {
                const temps: ItfData[] = generateItfDataArr(res);
                const items: ItfItem[] = generateItfItemArr(temps);
                generateItfGroupArr(items);
            })
            .catch((e) => {
                console.error(e);
            });
    }, []);

    useEffect(() => {
        const index = activeItem?.swiper?.findIndex((si: ItfSwiper) => si.color === activeItem?.color) || 0;
        swiper?.slideTo(index);
    }, [activeItem, swiper]);

    return (
        <section
            id="PowerSolution" // 由于运营已经配置好了 #PowerSolution
            className={`rapid-mag-C ${css.section} GtmModuleData-section`}
            data-section-id={'rapid-mag-C'}
            data-section-title={t('title')}
        >
            <div className="info">
                <p className="title">{t('title')}</p>
            </div>
            <div className="wrapper">
                <Swiper
                    modules={[Navigation, EffectFade]}
                    fadeEffect={{
                        crossFade: true,
                    }}
                    effect="fade"
                    className="swiper"
                    spaceBetween={0}
                    slidesPerView={1}
                    initialSlide={0}
                    navigation={
                        activeItem?.swiper?.length > 1
                            ? {
                                  prevEl: `.rapid-mag-C .swiper-button-prev`,
                                  nextEl: `.rapid-mag-C .swiper-button-next`,
                              }
                            : false
                    }
                    onSwiper={(swipwerIns: SwiperType) => setSwiper(swipwerIns)}
                    onSlideChange={handleSlideChange}
                >
                    {activeItem?.swiper?.map((si: ItfSwiper, k: number) => (
                        <SwiperSlide className="swiper-item" key={k}>
                            <div className="swiper-item-img">
                                <EcoImg pc={si.img} />
                            </div>
                        </SwiperSlide>
                    ))}
                    <ul className={`swiper-item-info${activePId === activeItem.productId ? ' active' : ''}`}>
                        {activeItem?.info?.map((ii: ItfInfo, m: number) => (
                            <li className={`swiper-item-info-sub swiper-item-info-sub-${m + 1}`} key={`${m}`}>
                                <div className="swiper-item-info-sub-info">
                                    <p className="swiper-item-info-sub-name">{ii.name}</p>
                                    <p className="swiper-item-info-sub-text">{ii.text}</p>
                                </div>
                                <p className="swiper-item-info-sub-line"></p>
                            </li>
                        ))}
                    </ul>
                    {activeItem?.swiper?.length > 1 && (
                        <div className="swiper-button">
                            <button
                                className={`swiper-button-prev swiper-button-btn GtmModuleData-btn-common ${active <= 0 ? 'swiper-button-disabled' : ''}`}
                                data-btn-id={'<'}
                                onClick={() => handleGTM(1)}
                            ></button>
                            <button
                                className={`swiper-button-next swiper-button-btn GtmModuleData-btn-common ${
                                    active >= items.length - 1 ? 'swiper-button-disabled' : ''
                                }`}
                                data-btn-id={'>'}
                                onClick={() => handleGTM(2)}
                            ></button>
                        </div>
                    )}
                </Swiper>
                <ul className="groups">
                    {groups.map((group: ItfItem[], k: number) => {
                        return (
                            <li
                                className={`group group-${k}${group?.find((item: ItfItem) => item.productId === activeItem.productId) ? ' active' : ''}`}
                                key={k}
                                style={{ width: `calc(100% / ${items?.length} * ${group.length})` }}
                            >
                                {groups?.length > 1 && (
                                    <div className={`group-head`}>
                                        <p className="group-head-title">{t(`series${k + 1}`)}</p>
                                        <p className={`group-head-line`}></p>
                                    </div>
                                )}
                                <ul className={`items ${groups?.length > 1 ? 'has-group-head' : 'no-group-head'}`}>
                                    {group.map((item: ItfItem, m: number) => {
                                        const isActiveItem = activeItem.productId === item.productId;
                                        return (
                                            <li
                                                className={`item item-${m}${isActiveItem ? ' active' : ''} pid-${item.productId}`}
                                                key={`${k}_${m}`}
                                                style={{ width: `calc(100% / ${group.length})` }}
                                                onMouseEnter={() => handleHover(item)}
                                            >
                                                <ul className="lists">
                                                    {item?.data?.map((list: ItfData, n: number) => {
                                                        const btnLearn = list.btns?.find((btn: ItfBtn) => btn.type === 'learn');
                                                        return (
                                                            <li
                                                                className={`list list-${n}${item.color === list.color ? ' active' : ''} pid-${
                                                                    item.productId
                                                                } vid-${list.variantId}`}
                                                                key={`${k}_${m}_${n}`}
                                                            >
                                                                <EcoLink
                                                                    className={`list-card GtmModuleData-btn-href cur-${curStage} allow-${list.allowLearnStage}`}
                                                                    href={btnLearn?.link || '/'}
                                                                    stopPropagation={Number(curStage) < list.allowLearnStage}
                                                                    preventDefault={Number(curStage) < list.allowLearnStage}
                                                                    dataLayer={{
                                                                        event: 'uaEvent',
                                                                        event_name: 'buy',
                                                                        product_name: list.productTitle,
                                                                        position: `Collections_${t('title')}`,
                                                                        __btn_id1: `.group-${k} .item-${m} .list-${n} .list-card`,
                                                                        __btn_id2: btnLearn?.link,
                                                                        __btn_type: 'href',
                                                                    }}
                                                                >
                                                                    <div className="list-img">
                                                                        <EcoImg pc={list.variantImg} />
                                                                    </div>
                                                                    <p className="list-tag">{list.tag}</p>
                                                                    <ul className="list-colors">
                                                                        {item?.swiper?.length > 1 &&
                                                                            item?.swiper?.map((c: ItfSwiper, o: number) => (
                                                                                <li
                                                                                    className={`list-color color-${o}${
                                                                                        list.color === c.color ? ' active' : ''
                                                                                    } GtmModuleData-btn-common`}
                                                                                    key={`${k}_${m}_${n}_${o}`}
                                                                                    data-btn-id={c.color}
                                                                                    style={{
                                                                                        border: `1px solid ${
                                                                                            list.color === c.color ? colorMapping[c.color] : 'transparent'
                                                                                        }`,
                                                                                    }}
                                                                                    onClick={(e: React.MouseEvent) =>
                                                                                        handleColorChange(
                                                                                            e,
                                                                                            k,
                                                                                            list,
                                                                                            c,
                                                                                            o,
                                                                                            `.group-${k} .item-${m} .list-${n} .color-${o}`,
                                                                                        )
                                                                                    }
                                                                                >
                                                                                    <span
                                                                                        className="circle"
                                                                                        style={{
                                                                                            // background: c.color,
                                                                                            // border: c.color === 'white' ? '1px solid #000' : 'none',
                                                                                            background: colorMapping[c.color],
                                                                                        }}
                                                                                    ></span>
                                                                                </li>
                                                                            ))}
                                                                    </ul>
                                                                    <p className="list-title">{list.productTitle}</p>
                                                                    <div className={`list-inter${curStage === 3 ? ' amazon-show' : ''}`}>
                                                                        {list.price > 0 && (
                                                                            <div className={`list-price ${!isActiveItem ? 'active' : ''}`}>
                                                                                <p className="price">{FormatMoneyLocal(list.price, locale)}</p>
                                                                                {list.price !== list.compareAtPrice && (
                                                                                    <p className="price-compare">
                                                                                        {FormatMoneyLocal(list.compareAtPrice, locale)}
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                        <ul className={`list-btns ${isActiveItem ? 'active' : ''}`}>
                                                                            {list?.stageMapping?.[curStage as number]?.map((btn: string) => {
                                                                                const isBtn =
                                                                                    ['notify'].includes(btn) ||
                                                                                    (['buy'].includes(btn) &&
                                                                                        list?.inventoryPolicy === 'deny' &&
                                                                                        list?.inventoryQuantity <= 0);
                                                                                const isLink =
                                                                                    !['notify', 'buy'].includes(btn) ||
                                                                                    (['buy'].includes(btn) &&
                                                                                        (list?.inventoryQuantity > 0 || list?.inventoryPolicy === 'continue'));
                                                                                const isAmazon = btn === 'amazon';
                                                                                const targetBtn = list.btns?.find((b: ItfBtn) => b.type === btn);
                                                                                return btn ? (
                                                                                    isLink ? (
                                                                                        <EcoLink
                                                                                            className={`list-btn ${btn}`}
                                                                                            key={`${k}_${m}_${n}_${btn}`}
                                                                                            stopPropagation
                                                                                            href={targetBtn?.link || '/'}
                                                                                            dataLayer={
                                                                                                isAmazon
                                                                                                    ? {
                                                                                                          event: 'uaEvent',
                                                                                                          event_name: 'buy_on_amazon',
                                                                                                          product_name: list.productTitle,
                                                                                                          button_name: t(`btn_${btn}`),
                                                                                                          list_name: t('title'),
                                                                                                          __btn_id1: `rapid-mag-C .group-${k} .item-${m} .list-${n} .${btn}`,
                                                                                                          __btn_id2: targetBtn?.link,
                                                                                                          __btn_type: 'href',
                                                                                                      }
                                                                                                    : {
                                                                                                          event: 'uaEvent',
                                                                                                          event_name: 'checkout',
                                                                                                          button_name: `${t(`btn_${btn}`)}_${
                                                                                                              list.productTitle
                                                                                                          }`,
                                                                                                          __btn_id1: `rapid-mag-C .group-${k} .item-${m} .list-${n} .${btn}`,
                                                                                                          __btn_id2: targetBtn?.link,
                                                                                                          __btn_type: 'href',
                                                                                                      }
                                                                                            }
                                                                                        >
                                                                                            {btn === 'amazon' && Amazon}
                                                                                            {t(`btn_${btn}`)}
                                                                                        </EcoLink>
                                                                                    ) : isBtn ? (
                                                                                        <button
                                                                                            className={`list-btn notify GtmModuleData-btn-common`}
                                                                                            key={`${k}_${m}_${n}_${btn}`}
                                                                                            data-btn-id={t('btn_notify')}
                                                                                            onClick={(e: React.MouseEvent) =>
                                                                                                handleOpenDialog(
                                                                                                    e,
                                                                                                    list.multiId,
                                                                                                    `.group-${k} .item-${m} .list-${n} .notify`,
                                                                                                )
                                                                                            }
                                                                                        >
                                                                                            {t(`btn_notify`)}
                                                                                        </button>
                                                                                    ) : (
                                                                                        <></>
                                                                                    )
                                                                                ) : (
                                                                                    <></>
                                                                                );
                                                                            })}
                                                                        </ul>
                                                                    </div>
                                                                </EcoLink>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </li>
                        );
                    })}
                </ul>
            </div>

            <Dialog open={Boolean(multiId)} multiId={multiId} onClose={() => setMultiId(0)} />
        </section>
    );
};

export default C;
